import { useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { Textarea } from "@/components/ui/textarea";
import { Flag, Ban } from "lucide-react";
import { toast } from "sonner";

const REASONS = [["harassment", "Harassment"], ["hate", "Hate"], ["sexual", "Sexual"],
                 ["spam", "Spam"], ["threat", "Threat"], ["other", "Other"]];

export const blockConfirmText = (name) => `Block ${name}? You won't see their messages. They won't be notified.`;

// Long-press (touch) or right-click opens the message action sheet.
export const useLongPress = (onLongPress, ms = 500) => {
  const timer = useRef(null);
  const clear = () => clearTimeout(timer.current);
  return {
    onTouchStart: () => { timer.current = setTimeout(onLongPress, ms); },
    onTouchEnd: clear, onTouchMove: clear,
    onContextMenu: (e) => { e.preventDefault(); onLongPress(); },
  };
};

// `post(kind, body)` performs the block/report call (staff vs homeowner endpoints differ).
export const BlockConfirm = ({ target, onClose, post, onChanged }) => (
  <AlertDialog open={!!target} onOpenChange={(o) => !o && onClose()}>
    <AlertDialogContent data-testid="block-confirm-dialog">
      <AlertDialogHeader>
        <AlertDialogTitle>Block user</AlertDialogTitle>
        <AlertDialogDescription>{target && blockConfirmText(target.sender_name)}</AlertDialogDescription>
      </AlertDialogHeader>
      <AlertDialogFooter>
        <AlertDialogCancel data-testid="block-cancel">Cancel</AlertDialogCancel>
        <AlertDialogAction className="bg-rose-600 hover:bg-rose-700" data-testid="block-confirm"
          onClick={async () => {
            try {
              await post("block", { message_id: target.id });
              toast.success(`${target.sender_name} blocked`);
              onChanged?.();
            } catch (e) { toast.error(e.message || "Could not block"); }
            onClose();
          }}>Block</AlertDialogAction>
      </AlertDialogFooter>
    </AlertDialogContent>
  </AlertDialog>
);

export const MessageActionSheet = ({ msg, onClose, post, onChanged }) => {
  const [mode, setMode] = useState("menu");
  const [reason, setReason] = useState("");
  const [details, setDetails] = useState("");
  const [busy, setBusy] = useState(false);
  const [blockTarget, setBlockTarget] = useState(null);

  const close = () => { setMode("menu"); setReason(""); setDetails(""); onClose(); };
  const report = async () => {
    setBusy(true);
    try {
      await post("report", { message_id: msg.id, reason, details });
      toast.success("Thanks. Our team will review this within 24 hours.");
      onChanged?.();
      close();
    } catch (e) { toast.error(e.message || "Could not report"); }
    finally { setBusy(false); }
  };

  return (
    <>
      <Dialog open={!!msg && !blockTarget} onOpenChange={(o) => !o && close()}>
        <DialogContent className="max-w-sm" data-testid="message-action-sheet">
          {mode === "menu" ? (
            <>
              <DialogHeader>
                <DialogTitle>Message from {msg?.sender_name}</DialogTitle>
                <DialogDescription className="line-clamp-2">{msg?.text || "Photo"}</DialogDescription>
              </DialogHeader>
              <Button variant="outline" className="h-11 justify-start" onClick={() => setMode("report")}
                      data-testid="message-action-report"><Flag className="w-4 h-4 mr-2" /> Report</Button>
              <Button variant="outline" className="h-11 justify-start text-rose-600" onClick={() => setBlockTarget(msg)}
                      data-testid="message-action-block"><Ban className="w-4 h-4 mr-2" /> Block</Button>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>Report message</DialogTitle>
                <DialogDescription>Why are you reporting this message?</DialogDescription>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-1.5">
                {REASONS.map(([value, label]) => (
                  <label key={value} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="radio" name="report-reason" checked={reason === value}
                           onChange={() => setReason(value)} data-testid={`report-reason-${value}`} />
                    {label}
                  </label>
                ))}
              </div>
              <Textarea placeholder="Details (optional)" value={details} maxLength={1000}
                        onChange={(e) => setDetails(e.target.value)} data-testid="report-details" />
              <Button onClick={report} disabled={!reason || busy} className="w-full bg-rose-600 hover:bg-rose-700 text-white"
                      data-testid="chat-report-submit">Submit report</Button>
            </>
          )}
        </DialogContent>
      </Dialog>
      <BlockConfirm target={blockTarget} post={post} onChanged={onChanged}
                    onClose={() => { setBlockTarget(null); close(); }} />
    </>
  );
};

export const LongPressBubble = ({ onLongPress, children, ...props }) => (
  <div {...useLongPress(onLongPress)} {...props}>{children}</div>
);

// Conversation menu → Block: lists the other participants in this chat.
export const ConversationBlockMenu = ({ messages, isMine, post, onChanged }) => {
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(null);
  const seen = new Set();
  const people = messages.filter((m) => !isMine(m)).filter((m) => {
    const k = m.sender_user_id || `homeowner:${m.sender_type}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="text-[10px] text-blue-700 font-bold flex items-center gap-1"
              data-testid="chat-conversation-menu"><Ban className="w-3 h-3" /> Block</button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-sm" data-testid="chat-conversation-block-dialog">
          <DialogHeader>
            <DialogTitle>Block someone in this chat</DialogTitle>
            <DialogDescription>{people.length ? "Choose who to block." : "No one else has sent a message yet."}</DialogDescription>
          </DialogHeader>
          {people.map((m) => (
            <Button key={m.id} variant="outline" className="h-11 justify-start" onClick={() => { setOpen(false); setTarget(m); }}
                    data-testid={`conversation-block-${m.id}`}>{m.sender_name}</Button>
          ))}
        </DialogContent>
      </Dialog>
      <BlockConfirm target={target} post={post} onChanged={onChanged} onClose={() => setTarget(null)} />
    </>
  );
};
